'use client';

import { memo, useCallback, useMemo } from 'react';

import { useAirModal } from '@air/provider-modal';

import { GeneralLibraryHeaderCreateButton } from '~/components/LibraryBeta/GeneralLibraryPage/GeneralLibraryHeaderCreateButton';
import { CreateBoardModal } from '~/components/Modals/CreateBoardModal';
import { CreateDropdownButton } from '~/components/Shared/HeaderBar/CreateDropdownButton';
import { useWorkspaceRightPanelOpen } from '~/components/v2/store/features/workspace/useWorkspaceRightPanelOpen';
import { useDraftCanvas } from '~/hooks/canvases/useDraftCanvas';
import { useTemporaryFeatureFlags } from '~/hooks/useTemporaryFeatureFlags';
import { useCurrentWorkspacePermissionsContext } from '~/providers/CurrentWorkspacePermissionsProvider';
import { getCreateMenuOptions } from '~/utils/dropdownMenuOptions/getCreateMenuOptions';
import { canCreateBoard } from '~/utils/permissions/boardPermissions';

const GeneralLibraryTopBarLegacyAddButton = memo(() => {
  const { data: permissions } = useCurrentWorkspacePermissionsContext();
  const canCreateRootBoards = canCreateBoard(permissions);
  const [showCreateBoardModal] = useAirModal(CreateBoardModal);
  const { generativeCanvasEnabled } = useTemporaryFeatureFlags();
  const isRightPanelOpen = useWorkspaceRightPanelOpen();
  const { openDraftCanvas } = useDraftCanvas();

  const handleCreateBoard = useCallback(() => {
    showCreateBoardModal({
      trackLocation: 'library',
    });
  }, [showCreateBoardModal]);

  const handleCreateCanvas = useCallback(() => {
    openDraftCanvas({
      analyticsContext: { source: 'general_library' },
    });
  }, [openDraftCanvas]);

  const options = useMemo(
    () =>
      getCreateMenuOptions({
        onCreateBoard: canCreateRootBoards ? handleCreateBoard : undefined,
        onCreateCanvas: generativeCanvasEnabled ? handleCreateCanvas : undefined,
      }),
    [canCreateRootBoards, generativeCanvasEnabled, handleCreateBoard, handleCreateCanvas],
  );

  if (isRightPanelOpen || options.length === 0) {
    return null;
  }

  return <CreateDropdownButton options={options} data-testid="GENERAL_LIBRARY_HEADER_ADD_BOARD_BUTTON" />;
});

GeneralLibraryTopBarLegacyAddButton.displayName = 'GeneralLibraryTopBarLegacyAddButton';

export const GeneralLibraryTopBarAddButton = memo(() => {
  const { ui2026 } = useTemporaryFeatureFlags();
  const { data: workspacePermissions } = useCurrentWorkspacePermissionsContext();

  if (!ui2026) {
    return <GeneralLibraryTopBarLegacyAddButton />;
  }

  return (
    <GeneralLibraryHeaderCreateButton
      data-testid="GENERAL_LIBRARY_HEADER_ADD_BOARD_BUTTON"
      workspacePermissions={workspacePermissions}
    />
  );
});

GeneralLibraryTopBarAddButton.displayName = 'GeneralLibraryTopBarAddButton';
